import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FieldArray, formValueSelector } from 'redux-form';
import { useSelector } from 'react-redux';
import { Grid, Field } from 'nallian-shared-ui/lib/components';
import { SelectFieldValueComponent } from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import PrincipalRoles from './PrincipalRoles';
import UserGroupRolesDisplay from './UserGroupRolesDisplay';
import FormHeader from '../../../../../portal/app/components/FormHeader';
import appSubscriptionsService from '../../../services/appSubscriptions/appSubscriptionsService';
import organisationUserGroupsService from '../../../services/userGroups/organisationUserGroupsService';
import organisationUserGroupsDetailsService from '../../../services/userGroups/organisationUserGroupsDetailsService';
import { PRINCIPAL_FORM_NAME, ASSIGNED_ROLES_TYPES } from '../../../constants';

const PrincipalRolesTab = ({
  appSubscriptionsFetch,
  dispatchAppSubscriptionsGet,
  dispatchOrganisationUserGroupsGet,
  organisationUserGroupsFetch,
  dispatchOrganisationUserGroupsDetailsPost,
  organisationUserGroupsDetailsFetch,
  organisationId,
  editMode,
  readOnly,
  formName,
  hideUserGroups,
  principal,
}) => {
  const { t } = useTranslation();
  const params = useParams();
  const id = organisationId || params.id;

  // Get selected user groups from form
  const selector = formValueSelector(formName || PRINCIPAL_FORM_NAME);
  const selectedUserGroups = useSelector(state => selector(state, 'userGroups'));

  // Extract user group IDs from principal's assignedRoles as fallback for edit mode (before form loads)
  const userGroupIdsFromPrincipal = useMemo(() => {
    if (!editMode || !principal?.assignedRoles) return [];

    const userGroupIds = new Set();
    (principal.assignedRoles || []).forEach(role => {
      const userGroupSources = role.sources?.filter(
        s => s.type === ASSIGNED_ROLES_TYPES.USER_GROUP
      );
      userGroupSources?.forEach(source => {
        if (source.id) userGroupIds.add(source.id);
      });
    });

    return Array.from(userGroupIds);
  }, [editMode, principal]);

  // Always use form values (selectedUserGroups), with fallback to extracted IDs in edit mode before form loads
  const activeUserGroupIds = selectedUserGroups || (editMode ? userGroupIdsFromPrincipal : []);

  useEffect(() => dispatchAppSubscriptionsGet(id, t), [t, id, dispatchAppSubscriptionsGet]);
  useEffect(() => dispatchOrganisationUserGroupsGet(id), [id, dispatchOrganisationUserGroupsGet]);

  // Fetch user group details when selected user groups change (or in edit mode with principal groups)
  useEffect(() => {
    if (activeUserGroupIds && activeUserGroupIds.length > 0) {
      dispatchOrganisationUserGroupsDetailsPost(id, activeUserGroupIds);
    }
  }, [activeUserGroupIds, id, dispatchOrganisationUserGroupsDetailsPost]);

  const apps = useMemo(
    () =>
      (appSubscriptionsFetch.value || []).map(app => ({
        ...app,
        key: `${app.appInstallation.tag}_${app.community.tag}`,
        value: `${app.appInstallation.tag}_${app.community.tag}`,
        text: `${app.alias ||
          app.appInstallation?.name ||
          app.appInstallation?.tag ||
          'Unknown App'}${app.community?.name ? ` (${app.community.name})` : ''}`,
      })),
    [appSubscriptionsFetch.value]
  );

  const userGroupOptions = useMemo(
    () =>
      (organisationUserGroupsFetch.value || []).map(oug => ({
        key: oug.id,
        value: oug.id,
        text: oug.name,
      })),
    [organisationUserGroupsFetch.value]
  );

  if (appSubscriptionsFetch.pending) return null;

  return (
    <>
      {userGroupOptions?.length > 0 && !hideUserGroups && (
        <>
          <FormHeader content={t('userGroups')} />
          <Grid.Column mobile={16} tablet={16} computer={16}>
            <Field
              fluid
              multiple
              search
              clearable
              placeholder={t('selectUserGroups')}
              name="userGroups"
              options={userGroupOptions}
              valueComponent={SelectFieldValueComponent}
              disabled={readOnly}
            />
          </Grid.Column>
          <UserGroupRolesDisplay
            userGroupsDetails={organisationUserGroupsDetailsFetch.value}
            loading={organisationUserGroupsDetailsFetch.pending}
            selectedUserGroupIds={activeUserGroupIds}
          />
          <FormHeader content={t('roles')} />
        </>
      )}
      <FieldArray
        name="communityApps"
        component={PrincipalRoles}
        editMode={editMode}
        apps={apps}
        readOnly={readOnly}
        formName={formName}
      />
    </>
  );
};

PrincipalRolesTab.propTypes = {
  readOnly: PropTypes.bool.isRequired,
  editMode: PropTypes.bool.isRequired,
  appSubscriptionsFetch: PropTypes.object.isRequired,
  dispatchAppSubscriptionsGet: PropTypes.func.isRequired,
  dispatchOrganisationUserGroupsGet: PropTypes.func.isRequired,
  organisationUserGroupsFetch: PropTypes.object.isRequired,
  dispatchOrganisationUserGroupsDetailsPost: PropTypes.func.isRequired,
  organisationUserGroupsDetailsFetch: PropTypes.object.isRequired,
  organisationId: PropTypes.string,
  formName: PropTypes.string,
  hideUserGroups: PropTypes.bool,
  principal: PropTypes.shape({
    assignedRoles: PropTypes.arrayOf(
      PropTypes.shape({
        sources: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            type: PropTypes.string,
          })
        ),
      })
    ),
  }),
};

PrincipalRolesTab.defaultProps = {
  hideUserGroups: false,
  organisationId: undefined,
  formName: undefined,
  principal: undefined,
};

export default compose(
  organisationUserGroupsService,
  organisationUserGroupsDetailsService,
  appSubscriptionsService
)(PrincipalRolesTab);
